import React, { useState, useEffect } from 'react';
import { Server, Globe, Network, Cpu, Tag, Search, Plus, X, Edit2, Trash2, Shield, AlertTriangle, ChevronRight, ExternalLink, GitGraph } from 'lucide-react';
import { useAuth } from '@clerk/clerk-react';
import { useTenant } from '../TenantProvider';
import { 
  AddDomainModal, 
  AddSubdomainModal, 
  AddIPModal, 
  AddTechnologyModal, 
  AddKeywordModal 
} from './AssetModals';
import RelationshipGraphView from './RelationshipGraphView';

const AssetsView = ({ isSuperAdmin, canSeeSuperAdminSettings }) => {
  const { getToken, isSignedIn } = useAuth();
  const { activeCompany } = useTenant();

  const [activeTab, setActiveTab] = useState('domains');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Data states
  const [domains, setDomains] = useState([]);
  const [subdomains, setSubdomains] = useState([]);
  const [ipAddresses, setIpAddresses] = useState([]);
  const [technologies, setTechnologies] = useState([]);
  const [keywords, setKeywords] = useState([]);
  
  // Selected items
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [selectedSubdomain, setSelectedSubdomain] = useState(null);
  const [selectedIP, setSelectedIP] = useState(null);
  const [selectedTechnology, setSelectedTechnology] = useState(null);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  
  // IP Details data
  const [ipWhois, setIpWhois] = useState(null);
  const [ipPorts, setIpPorts] = useState([]);
  const [ipVulns, setIpVulns] = useState([]);
  const [ipHostedDomains, setIpHostedDomains] = useState([]);
  const [ipTechnologies, setIpTechnologies] = useState([]);
  
  // Domain Details data
  const [domainDNS, setDomainDNS] = useState([]);
  const [domainSubdomains, setDomainSubdomains] = useState([]);
  
  // Modal states
  const [showAddDomain, setShowAddDomain] = useState(false);
  const [showAddSubdomain, setShowAddSubdomain] = useState(false);
  const [showAddIP, setShowAddIP] = useState(false);
  const [showAddTechnology, setShowAddTechnology] = useState(false);
  const [showAddKeyword, setShowAddKeyword] = useState(false);
  
  // Edit states
  const [isEditing, setIsEditing] = useState(false);

  // Load all assets on mount
  useEffect(() => {
    if (!isSignedIn || !activeCompany) {
      console.log('Not ready:', { isSignedIn, activeCompany });
      return;
    }
    console.log('Loading assets for company:', activeCompany.id);
    loadAllAssets();
  }, [isSignedIn, activeCompany, getToken]);

  const loadAllAssets = async () => {
    setLoading(true);
    try {
      const token = await getToken();
      
      console.log('Fetching assets for company:', activeCompany.id);
      
      // Load all asset types in parallel
      const [domainsRes, subdomainsRes, ipsRes, techRes, keywordsRes] = await Promise.all([
        fetch(`/api/companies/${activeCompany.id}/assets/domains`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/companies/${activeCompany.id}/assets/subdomains`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/companies/${activeCompany.id}/assets/ips`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/companies/${activeCompany.id}/assets/technologies`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }),
        fetch(`/api/companies/${activeCompany.id}/assets/keywords`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
      ]);

      console.log('Response statuses:', {
        domains: domainsRes.status,
        subdomains: subdomainsRes.status,
        ips: ipsRes.status,
        tech: techRes.status,
        keywords: keywordsRes.status
      });

      if (domainsRes.ok) {
        const data = await domainsRes.json();
        console.log('Domains loaded:', data.length);
        setDomains(data);
        if (data.length > 0) setSelectedDomain(data[0]);
      }
      if (subdomainsRes.ok) {
        const data = await subdomainsRes.json();
        console.log('Subdomains loaded:', data.length);
        setSubdomains(data);
      }
      if (ipsRes.ok) {
        const data = await ipsRes.json();
        console.log('IPs loaded:', data.length);
        setIpAddresses(data);
      }
      if (techRes.ok) {
        const data = await techRes.json();
        console.log('Technologies loaded:', data.length);
        setTechnologies(data);
      }
      if (keywordsRes.ok) {
        const data = await keywordsRes.json();
        console.log('Keywords loaded:', data.length);
        setKeywords(data);
      }
    } catch (error) {
      console.error('Error loading assets:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch IP details when IP is selected
  useEffect(() => {
    if (selectedIP && activeTab === 'ips') {
      fetchIPDetails(selectedIP.ip_id);
    }
  }, [selectedIP]);

  // Fetch Domain details when domain is selected
  useEffect(() => {
    if (selectedDomain && activeTab === 'domains') {
      fetchDomainDetails(selectedDomain.domain_id);
    }
  }, [selectedDomain]);

  const fetchIPDetails = async (ipId) => {
    try {
      const token = await getToken();
      const baseUrl = `/api/companies/${activeCompany.id}/assets/ips/${ipId}`;
      
      const [whoisRes, portsRes, vulnsRes, domainsRes, techRes] = await Promise.all([
        fetch(`${baseUrl}/whois`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${baseUrl}/ports`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${baseUrl}/vulnerabilities`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${baseUrl}/hosted-domains`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${baseUrl}/technologies`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (whoisRes.ok) setIpWhois(await whoisRes.json());
      if (portsRes.ok) setIpPorts(await portsRes.json());
      if (vulnsRes.ok) setIpVulns(await vulnsRes.json());
      if (domainsRes.ok) setIpHostedDomains(await domainsRes.json());
      if (techRes.ok) setIpTechnologies(await techRes.json());
    } catch (error) {
      console.error('Error fetching IP details:', error);
    }
  };

  const fetchDomainDetails = async (domainId) => {
    try {
      const token = await getToken();
      const baseUrl = `/api/companies/${activeCompany.id}/assets/domains/${domainId}`;
      
      const [dnsRes, subdomainsRes] = await Promise.all([
        fetch(`${baseUrl}/dns`, { headers: { 'Authorization': `Bearer ${token}` } }),
        fetch(`${baseUrl}/subdomains`, { headers: { 'Authorization': `Bearer ${token}` } })
      ]);
      
      if (dnsRes.ok) setDomainDNS(await dnsRes.json());
      if (subdomainsRes.ok) setDomainSubdomains(await subdomainsRes.json());
    } catch (error) {
      console.error('Error fetching domain details:', error);
    }
  };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setSearchTerm('');
    
    // Restore selections for tabs with data
    if (tab === 'domains' && domains.length > 0 && !selectedDomain) {
      setSelectedDomain(domains[0]);
    } else if (tab === 'subdomains' && subdomains.length > 0 && !selectedSubdomain) {
      setSelectedSubdomain(subdomains[0]);
    } else if (tab === 'ips' && ipAddresses.length > 0 && !selectedIP) {
      setSelectedIP(ipAddresses[0]);
    } else if (tab === 'technologies' && technologies.length > 0 && !selectedTechnology) {
      setSelectedTechnology(technologies[0]);
    } else if (tab === 'keywords' && keywords.length > 0 && !selectedKeyword) {
      setSelectedKeyword(keywords[0]);
    } else if (tab !== 'graph') {
      setSelectedDomain(null);
      setSelectedSubdomain(null);
      setSelectedIP(null);
      setSelectedTechnology(null);
      setSelectedKeyword(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      const token = await getToken();
      let endpoint = '';
      
      switch(activeTab) {
        case 'domains': endpoint = `/api/companies/${activeCompany.id}/assets/domains/${id}`; break;
        case 'subdomains': endpoint = `/api/companies/${activeCompany.id}/assets/subdomains/${id}`; break;
        case 'ips': endpoint = `/api/companies/${activeCompany.id}/assets/ips/${id}`; break;
        case 'technologies': endpoint = `/api/companies/${activeCompany.id}/assets/technologies/${id}`; break;
        case 'keywords': endpoint = `/api/companies/${activeCompany.id}/assets/keywords/${id}`; break;
      }
      
      const response = await fetch(endpoint, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        alert('Item deleted successfully');
        loadAllAssets();
      }
    } catch (error) {
      console.error('Error deleting item:', error);
      alert('Error deleting item');
    }
  };

  const renderTabButton = (tab, icon, label, count) => (
    <button
      onClick={() => handleTabChange(tab)}
      className={`flex items-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
        activeTab === tab
          ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
          : 'text-slate-400 hover:bg-slate-800/50 hover:text-slate-300 border border-transparent'
      }`}
    >
      {icon}
      <span>{label}</span>
      {count !== undefined && (
        <span className="ml-1 px-2 py-0.5 bg-slate-700/50 rounded-full text-xs">
          {count}
        </span>
      )}
    </button>
  );

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Header */}
      <div className="px-6 py-4 border-b border-slate-800/50 bg-slate-900/30">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-2xl font-bold text-slate-100 flex items-center gap-3">
              <Server className="w-7 h-7 text-cyan-400" />
              Asset Management
            </h2>
            <p className="text-sm text-slate-400 mt-1">
              Monitor and manage your organization's digital assets
            </p>
          </div>
          
          {isSuperAdmin && (
            <button
              onClick={() => {
                switch(activeTab) {
                  case 'domains': setShowAddDomain(true); break;
                  case 'subdomains': setShowAddSubdomain(true); break;
                  case 'ips': setShowAddIP(true); break;
                  case 'technologies': setShowAddTechnology(true); break;
                  case 'keywords': setShowAddKeyword(true); break;
                }
              }}
              className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white rounded-lg text-sm font-medium transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Add {activeTab === 'ips' ? 'IP' : activeTab.slice(0, -1).charAt(0).toUpperCase() + activeTab.slice(1, -1)}
            </button>
          )}
        </div>

        {/* Sub-navigation */}
        <div className="flex items-center gap-2 flex-wrap">
          {renderTabButton('domains', <Globe className="w-4 h-4" />, 'Domains', domains.length)}
          {renderTabButton('subdomains', <Network className="w-4 h-4" />, 'Subdomains', subdomains.length)}
          {renderTabButton('ips', <Server className="w-4 h-4" />, 'IP Addresses', ipAddresses.length)}
          {renderTabButton('technologies', <Cpu className="w-4 h-4" />, 'Technologies', technologies.length)}
          {renderTabButton('keywords', <Tag className="w-4 h-4" />, 'Keywords', keywords.length)}
          {renderTabButton('graph', <GitGraph className="w-4 h-4" />, 'Relationship Graph')}
        </div>
      </div>

      {/* Content Area */}
      <div className="flex-1 flex overflow-hidden">
        {/* Show split-panel view for normal tabs */}
        {activeTab !== 'graph' && (
          <>
            {/* Left Sidebar - List */}
            <div className="w-[420px] border-r border-slate-800/50 bg-slate-900/30 flex flex-col">
              {/* Search */}
              <div className="p-4 border-b border-slate-800/50">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <input
                    type="text"
                    placeholder={`Search ${activeTab}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg text-sm text-slate-200 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-cyan-500/50"
                  />
                </div>
              </div>

              {/* List */}
              <div className="flex-1 overflow-y-auto">
                {loading ? (
                  <div className="p-8 text-center text-slate-400">Loading...</div>
                ) : (
                  <>
                    {activeTab === 'domains' && <DomainsList 
                      domains={domains.filter(d => d.domain?.toLowerCase().includes(searchTerm.toLowerCase()))}
                      selected={selectedDomain}
                      onSelect={setSelectedDomain}
                    />}
                    
                    {activeTab === 'subdomains' && <SubdomainsList 
                      subdomains={subdomains.filter(s => s.full_subdomain?.toLowerCase().includes(searchTerm.toLowerCase()))}
                      selected={selectedSubdomain}
                      onSelect={setSelectedSubdomain}
                    />}
                    
                    {activeTab === 'ips' && <IPsList 
                      ips={ipAddresses.filter(ip => ip.ip_address?.toLowerCase().includes(searchTerm.toLowerCase()))}
                      selected={selectedIP}
                      onSelect={setSelectedIP}
                    />}
                    
                    {activeTab === 'technologies' && <TechnologiesList 
                      technologies={technologies.filter(t => t.technology_name?.toLowerCase().includes(searchTerm.toLowerCase()))}
                      selected={selectedTechnology}
                      onSelect={setSelectedTechnology}
                    />}
                    
                    {activeTab === 'keywords' && <KeywordsList 
                      keywords={keywords.filter(k => k.keyword?.toLowerCase().includes(searchTerm.toLowerCase()))}
                      selected={selectedKeyword}
                      onSelect={setSelectedKeyword}
                    />}
                  </>
                )}
              </div>
            </div>

            {/* Right Panel - Details */}
            <div className="flex-1 overflow-y-auto bg-slate-900/20">
              {activeTab === 'domains' && selectedDomain && (
                <DomainDetails 
                  domain={selectedDomain} 
                  dnsRecords={domainDNS}
                  subdomains={domainSubdomains}
                  onRefresh={loadAllAssets}
                  onDelete={() => handleDelete(selectedDomain.domain_id)}
                  isSuperAdmin={isSuperAdmin}
                />
              )}
              {activeTab === 'subdomains' && selectedSubdomain && (
                <SubdomainDetails 
                  subdomain={selectedSubdomain} 
                  onRefresh={loadAllAssets}
                  onDelete={() => handleDelete(selectedSubdomain.subdomain_id)}
                  isSuperAdmin={isSuperAdmin}
                />
              )}
              {activeTab === 'ips' && selectedIP && (
                <IPDetails 
                  ip={selectedIP}
                  whois={ipWhois}
                  ports={ipPorts}
                  vulnerabilities={ipVulns}
                  hostedDomains={ipHostedDomains}
                  technologies={ipTechnologies}
                  onRefresh={loadAllAssets}
                  onDelete={() => handleDelete(selectedIP.ip_id)}
                  isSuperAdmin={isSuperAdmin}
                />
              )}
              {activeTab === 'technologies' && selectedTechnology && (
                <TechnologyDetails 
                  technology={selectedTechnology} 
                  onRefresh={loadAllAssets}
                  onDelete={() => handleDelete(selectedTechnology.tech_id)}
                  isSuperAdmin={isSuperAdmin}
                />
              )}
              {activeTab === 'keywords' && selectedKeyword && (
                <KeywordDetails 
                  keyword={selectedKeyword} 
                  onRefresh={loadAllAssets}
                  onDelete={() => handleDelete(selectedKeyword.keyword_id)}
                  isSuperAdmin={isSuperAdmin}
                />
              )}
              
              {!selectedDomain && !selectedSubdomain && !selectedIP && !selectedTechnology && !selectedKeyword && (
                <div className="h-full flex items-center justify-center text-slate-500">
                  <div className="text-center">
                    <Server className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>Select an item to view details</p>
                  </div>
                </div>
              )}
            </div>
          </>
        )}

        {/* Show graph view for graph tab */}
        {activeTab === 'graph' && (
          <RelationshipGraphView 
            domains={domains}
            subdomains={subdomains}
            ipAddresses={ipAddresses}
          />
        )}
      </div>
    </div>
  );
};

const DomainsList = ({ domains, selected, onSelect }) => (
  <div className="p-2">
    {domains.length === 0 ? (
      <div className="p-8 text-center text-slate-500">No domains found</div>
    ) : (
      domains.map((domain) => (
        <div
          key={domain.domain_id}
          onClick={() => onSelect(domain)}
          className={`p-4 mb-2 rounded-lg cursor-pointer transition-all ${
            selected?.domain_id === domain.domain_id
              ? 'bg-cyan-500/20 border border-cyan-500/30'
              : 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50'
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-slate-200 truncate">{domain.domain}</div>
              <div className="text-xs text-slate-500 mt-1">
                {domain.registrar || 'Unknown Registrar'}
              </div>
            </div>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              domain.status === 'Active' ? 'bg-green-500/20 text-green-400' :
              domain.status === 'Expired' ? 'bg-red-500/20 text-red-400' :
              'bg-yellow-500/20 text-yellow-400'
            }`}>
              {domain.status}
            </span>
          </div>
        </div>
      ))
    )}
  </div>
);

const SubdomainsList = ({ subdomains, selected, onSelect }) => (
  <div className="p-2">
    {subdomains.length === 0 ? (
      <div className="p-8 text-center text-slate-500">No subdomains found</div>
    ) : (
      subdomains.map((subdomain) => (
        <div
          key={subdomain.subdomain_id}
          onClick={() => onSelect(subdomain)}
          className={`p-4 mb-2 rounded-lg cursor-pointer transition-all ${
            selected?.subdomain_id === subdomain.subdomain_id
              ? 'bg-cyan-500/20 border border-cyan-500/30'
              : 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50'
          }`}
        >
          <div className="font-medium text-slate-200 truncate">{subdomain.full_subdomain}</div>
          <div className="text-xs text-slate-400 mt-1 font-mono">{subdomain.ip_address}</div>
          <span className={`inline-block mt-2 px-2 py-1 rounded text-xs font-medium ${
            subdomain.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'
          }`}>
            {subdomain.status}
          </span>
        </div>
      ))
    )}
  </div>
);

const IPsList = ({ ips, selected, onSelect }) => (
  <div className="p-2">
    {ips.length === 0 ? (
      <div className="p-8 text-center text-slate-500">No IP addresses found</div>
    ) : (
      ips.map((ip) => (
        <div
          key={ip.ip_id}
          onClick={() => onSelect(ip)}
          className={`p-4 mb-2 rounded-lg cursor-pointer transition-all ${
            selected?.ip_id === ip.ip_id
              ? 'bg-cyan-500/20 border border-cyan-500/30'
              : 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50'
          }`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="font-mono text-slate-200 text-sm">{ip.ip_address}</div>
              <div className="text-xs text-slate-400 mt-1">{ip.owner || 'Unknown Owner'}</div>
              {ip.country && (
                <div className="text-xs text-slate-500 mt-1">{ip.country}</div>
              )}
            </div>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              ip.status === 'Active' ? 'bg-green-500/20 text-green-400' : 'bg-slate-500/20 text-slate-400'
            }`}>
              {ip.status}
            </span>
          </div>
        </div>
      ))
    )}
  </div>
);

const TechnologiesList = ({ technologies, selected, onSelect }) => (
  <div className="p-2">
    {technologies.length === 0 ? (
      <div className="p-8 text-center text-slate-500">No technologies found</div>
    ) : (
      technologies.map((tech) => (
        <div
          key={tech.tech_id}
          onClick={() => onSelect(tech)}
          className={`p-4 mb-2 rounded-lg cursor-pointer transition-all ${
            selected?.tech_id === tech.tech_id
              ? 'bg-cyan-500/20 border border-cyan-500/30'
              : 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50'
          }`}
        >
          <div className="font-medium text-slate-200">{tech.technology_name}</div>
          {tech.version && (
            <div className="text-xs text-slate-400 mt-1">Version: {tech.version}</div>
          )}
          <div className="text-xs text-slate-500 mt-1">{tech.category}</div>
          {tech.ip_address && (
            <div className="text-xs text-cyan-400 mt-2 font-mono">{tech.ip_address}</div>
          )}
        </div>
      ))
    )}
  </div>
);

const KeywordsList = ({ keywords, selected, onSelect }) => (
  <div className="p-2">
    {keywords.length === 0 ? (
      <div className="p-8 text-center text-slate-500">No keywords found</div>
    ) : (
      keywords.map((keyword) => (
        <div
          key={keyword.keyword_id}
          onClick={() => onSelect(keyword)}
          className={`p-4 mb-2 rounded-lg cursor-pointer transition-all ${
            selected?.keyword_id === keyword.keyword_id
              ? 'bg-cyan-500/20 border border-cyan-500/30'
              : 'bg-slate-800/30 border border-slate-700/50 hover:bg-slate-800/50'
          }`}
        >
          <div className="flex items-start justify-between">
            <div>
              <div className="font-medium text-slate-200">{keyword.keyword}</div>
              <div className="text-xs text-slate-400 mt-1">{keyword.category}</div>
            </div>
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              keyword.priority === 'High' ? 'bg-red-500/20 text-red-400' :
              keyword.priority === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-green-500/20 text-green-400'
            }`}>
              {keyword.priority}
            </span>
          </div>
        </div>
      ))
    )}
  </div>
);

// ========================================
// DETAIL COMPONENTS
// ========================================

const DomainDetails = ({ domain, dnsRecords, subdomains, onRefresh, onDelete, isSuperAdmin }) => (
  <div className="p-6">
    <div className="flex items-start justify-between mb-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-100">{domain.domain}</h3>
        <p className="text-sm text-slate-400 mt-1">Domain Information</p>
      </div>
      {isSuperAdmin && (
        <button
          onClick={onDelete}
          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      )}
    </div>

    <div className="space-y-6">
      {/* Basic Info */}
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Basic Information
        </h4>
        <div className="space-y-2">
          <DetailRow label="Registrar" value={domain.registrar} />
          <DetailRow label="Status" value={<StatusBadge status={domain.status} />} />
          <DetailRow label="Registered" value={domain.registered_date ? new Date(domain.registered_date).toLocaleDateString() : 'N/A'} />
          <DetailRow label="Expires" value={domain.expiry_date ? new Date(domain.expiry_date).toLocaleDateString() : 'N/A'} />
        </div>
      </div>

      {/* DNS Records */}
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Network className="w-4 h-4" />
          DNS Records ({dnsRecords.length})
        </h4>
        {dnsRecords.length === 0 ? (
          <p className="text-sm text-slate-500">No DNS records found</p>
        ) : (
          <div className="space-y-2">
            {dnsRecords.map((record, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0">
                <div>
                  <span className="text-xs font-mono bg-slate-700/50 px-2 py-1 rounded text-cyan-400">
                    {record.record_type}
                  </span>
                  <span className="text-sm text-slate-300 ml-3">{record.record_name || '@'}</span>
                </div>
                <span className="text-sm text-slate-400 font-mono">{record.record_value}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Subdomains */}
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <ChevronRight className="w-4 h-4" />
          Subdomains ({subdomains.length})
        </h4>
        {subdomains.length === 0 ? (
          <p className="text-sm text-slate-500">No subdomains found</p>
        ) : (
          <div className="space-y-2">
            {subdomains.map((sub) => (
              <div key={sub.subdomain_id} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0">
                <span className="text-sm text-slate-300">{sub.full_subdomain}</span>
                <span className="text-xs text-slate-400 font-mono">{sub.ip_address}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {domain.notes && (
        <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
          <h4 className="text-sm font-semibold text-slate-300 mb-2">Notes</h4>
          <p className="text-sm text-slate-400">{domain.notes}</p>
        </div>
      )}
    </div>
  </div>
);

const SubdomainDetails = ({ subdomain, onRefresh, onDelete, isSuperAdmin }) => (
  <div className="p-6">
    <div className="flex items-start justify-between mb-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-100">{subdomain.full_subdomain}</h3>
        <p className="text-sm text-slate-400 mt-1">Subdomain Information</p>
      </div>
      {isSuperAdmin && (
        <button
          onClick={onDelete}
          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      )}
    </div>

    <div className="space-y-6">
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <div className="space-y-2">
          <DetailRow label="Full Subdomain" value={subdomain.full_subdomain} />
          <DetailRow label="IP Address" value={<span className="font-mono text-cyan-400">{subdomain.ip_address}</span>} />
          <DetailRow label="Status" value={<StatusBadge status={subdomain.status} />} />
          <DetailRow label="Discovered" value={new Date(subdomain.discovered_date).toLocaleDateString()} />
          <DetailRow label="Last Seen" value={new Date(subdomain.last_seen).toLocaleDateString()} />
        </div>
      </div>

      {subdomain.notes && (
        <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
          <h4 className="text-sm font-semibold text-slate-300 mb-2">Notes</h4>
          <p className="text-sm text-slate-400">{subdomain.notes}</p>
        </div>
      )}
    </div>
  </div>
);

const IPDetails = ({ ip, whois, ports, vulnerabilities, hostedDomains, technologies, onRefresh, onDelete, isSuperAdmin }) => (
  <div className="p-6">
    <div className="flex items-start justify-between mb-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-100 font-mono">{ip.ip_address}</h3>
        <p className="text-sm text-slate-400 mt-1">IP Address Information</p>
      </div>
      {isSuperAdmin && (
        <button
          onClick={onDelete}
          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      )}
    </div>

    <div className="space-y-6">
      {/* Basic Info */}
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Server className="w-4 h-4" />
          Basic Information
        </h4>
        <div className="space-y-2">
          <DetailRow label="Owner" value={ip.owner} />
          <DetailRow label="ASN" value={ip.asn} />
          <DetailRow label="Country" value={ip.country} />
          <DetailRow label="City" value={ip.city} />
          <DetailRow label="ISP" value={ip.isp} />
          <DetailRow label="Status" value={<StatusBadge status={ip.status} />} />
        </div>
      </div>

      {/* Open Ports */}
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Network className="w-4 h-4" />
          Open Ports ({ports.length})
        </h4>
        {ports.length === 0 ? (
          <p className="text-sm text-slate-500">No open ports detected</p>
        ) : (
          <div className="space-y-2">
            {ports.map((port) => (
              <div key={port.port_id} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0">
                <div>
                  <span className="text-sm font-mono text-cyan-400">{port.port_number}/{port.protocol}</span>
                  <span className="text-sm text-slate-400 ml-3">{port.service}</span>
                </div>
                <span className="text-xs text-slate-500">{port.version}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Vulnerabilities */}
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Vulnerabilities ({vulnerabilities.length})
        </h4>
        {vulnerabilities.length === 0 ? (
          <p className="text-sm text-slate-500">No vulnerabilities detected</p>
        ) : (
          <div className="space-y-3">
            {vulnerabilities.map((vuln) => (
              <div key={vuln.vuln_id} className="p-3 bg-slate-900/50 rounded border border-slate-700/30">
                <div className="flex items-start justify-between mb-2">
                  <span className="text-sm font-mono text-cyan-400">{vuln.cve_id}</span>
                  <span className={`px-2 py-1 rounded text-xs font-medium ${
                    vuln.severity === 'Critical' ? 'bg-red-500/20 text-red-400' :
                    vuln.severity === 'High' ? 'bg-orange-500/20 text-orange-400' :
                    vuln.severity === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {vuln.severity} - {vuln.cvss_score}
                  </span>
                </div>
                <p className="text-sm text-slate-300">{vuln.title}</p>
                <p className="text-xs text-slate-500 mt-1">{vuln.description}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Hosted Domains */}
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Globe className="w-4 h-4" />
          Hosted Domains/Subdomains ({hostedDomains.length})
        </h4>
        {hostedDomains.length === 0 ? (
          <p className="text-sm text-slate-500">No domains hosted on this IP</p>
        ) : (
          <div className="space-y-2">
            {hostedDomains.map((domain, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0">
                <span className="text-sm text-slate-300">{domain.domain || domain.subdomain}</span>
                <span className="text-xs text-slate-500">{new Date(domain.last_verified).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Technologies */}
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3 flex items-center gap-2">
          <Cpu className="w-4 h-4" />
          Technologies ({technologies.length})
        </h4>
        {technologies.length === 0 ? (
          <p className="text-sm text-slate-500">No technologies detected</p>
        ) : (
          <div className="space-y-2">
            {technologies.map((tech) => (
              <div key={tech.tech_id} className="flex items-center justify-between py-2 border-b border-slate-700/30 last:border-0">
                <div>
                  <span className="text-sm text-slate-300">{tech.technology_name}</span>
                  {tech.version && <span className="text-xs text-slate-500 ml-2">v{tech.version}</span>}
                </div>
                <span className="text-xs text-slate-400">{tech.category}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {ip.notes && (
        <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
          <h4 className="text-sm font-semibold text-slate-300 mb-2">Notes</h4>
          <p className="text-sm text-slate-400">{ip.notes}</p>
        </div>
      )}
    </div>
  </div>
);

const TechnologyDetails = ({ technology, onRefresh, onDelete, isSuperAdmin }) => (
  <div className="p-6">
    <div className="flex items-start justify-between mb-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-100">{technology.technology_name}</h3>
        <p className="text-sm text-slate-400 mt-1">Technology Information</p>
      </div>
      {isSuperAdmin && (
        <button
          onClick={onDelete}
          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      )}
    </div>

    <div className="space-y-6">
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <div className="space-y-2">
          <DetailRow label="Technology" value={technology.technology_name} />
          <DetailRow label="Category" value={technology.category} />
          <DetailRow label="Version" value={technology.version} />
          <DetailRow label="IP Address" value={technology.ip_address ? <span className="font-mono text-cyan-400">{technology.ip_address}</span> : 'N/A'} />
          <DetailRow label="Confidence" value={technology.confidence} />
          <DetailRow label="Detected" value={new Date(technology.detected_date).toLocaleDateString()} />
          <DetailRow label="Last Seen" value={new Date(technology.last_seen).toLocaleDateString()} />
        </div>
      </div>

      {technology.notes && (
        <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
          <h4 className="text-sm font-semibold text-slate-300 mb-2">Notes</h4>
          <p className="text-sm text-slate-400">{technology.notes}</p>
        </div>
      )}
    </div>
  </div>
);

const KeywordDetails = ({ keyword, onRefresh, onDelete, isSuperAdmin }) => (
  <div className="p-6">
    <div className="flex items-start justify-between mb-6">
      <div>
        <h3 className="text-2xl font-bold text-slate-100">{keyword.keyword}</h3>
        <p className="text-sm text-slate-400 mt-1">Keyword Monitoring</p>
      </div>
      {isSuperAdmin && (
        <button
          onClick={onDelete}
          className="px-3 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg text-sm font-medium transition-all flex items-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete
        </button>
      )}
    </div>

    <div className="space-y-6">
      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <div className="space-y-2">
          <DetailRow label="Keyword" value={keyword.keyword} />
          <DetailRow label="Category" value={keyword.category} />
          <DetailRow label="Priority" value={
            <span className={`px-2 py-1 rounded text-xs font-medium ${
              keyword.priority === 'High' ? 'bg-red-500/20 text-red-400' :
              keyword.priority === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
              'bg-green-500/20 text-green-400'
            }`}>
              {keyword.priority}
            </span>
          } />
        </div>
      </div>

      <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
        <h4 className="text-sm font-semibold text-slate-300 mb-3">Monitoring Settings</h4>
        <div className="space-y-2">
          <DetailRow label="Dark Web Monitoring" value={keyword.monitor_dark_web ? 'âœ“ Enabled' : 'âœ— Disabled'} />
          <DetailRow label="Social Media Monitoring" value={keyword.monitor_social_media ? 'âœ“ Enabled' : 'âœ— Disabled'} />
          <DetailRow label="Domain Monitoring" value={keyword.monitor_domains ? 'âœ“ Enabled' : 'âœ— Disabled'} />
        </div>
      </div>

      {keyword.notes && (
        <div className="bg-slate-800/30 rounded-lg p-4 border border-slate-700/50">
          <h4 className="text-sm font-semibold text-slate-300 mb-2">Notes</h4>
          <p className="text-sm text-slate-400">{keyword.notes}</p>
        </div>
      )}
    </div>
  </div>
);

// Helper Components
const DetailRow = ({ label, value }) => (
  <div className="flex items-start justify-between py-2 border-b border-slate-700/30 last:border-0">
    <span className="text-sm text-slate-400">{label}</span>
    <span className="text-sm text-slate-200 font-medium text-right">{value || 'N/A'}</span>
  </div>
);

const StatusBadge = ({ status }) => (
  <span className={`px-2 py-1 rounded text-xs font-medium ${
    status === 'Active' ? 'bg-green-500/20 text-green-400' :
    status === 'Expired' ? 'bg-red-500/20 text-red-400' :
    status === 'Suspended' ? 'bg-yellow-500/20 text-yellow-400' :
    'bg-slate-500/20 text-slate-400'
  }`}>
    {status}
  </span>
);

export default AssetsView;